import { Plus, Trash2 } from 'lucide-react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { MAX_PARCELS, type WorkflowFormInput } from '../schemas/workflow-form-schema';

export interface ParcelsEditorProps {
  stageIndex: number;
  disabled?: boolean;
}

/** Shipping stage parcels; at most one carries the order items and creates the fulfilment. */
export function ParcelsEditor({ stageIndex, disabled = false }: ParcelsEditorProps) {
  const { t } = useTranslation('workflows');
  const form = useFormContext<WorkflowFormInput>();
  const name = `stages.${stageIndex}.parcels` as const;
  const { fields, append, remove } = useFieldArray({ control: form.control, name });
  const labels = useWatch({ control: form.control, name }).map((parcel) => parcel.label);
  const stageErrors = form.formState.errors.stages?.[stageIndex]?.parcels;
  const arrayError = stageErrors?.message ?? stageErrors?.root?.message;

  return (
    <fieldset className="flex flex-col gap-3 rounded-md border p-3">
      <legend className="px-1 text-sm font-medium">{t('parcels.title')}</legend>
      <p className="text-muted-foreground text-xs">{t('parcels.description')}</p>
      {fields.length === 0 ? (
        <p className="text-muted-foreground text-xs">{t('parcels.emptyHint')}</p>
      ) : null}
      <ul className="flex flex-col gap-2" aria-label={t('parcels.title')}>
        {fields.map((item, index) => {
          const label = labels[index] ?? '';
          return (
            <li key={item.id} className="flex flex-wrap items-start gap-3">
              <FormField
                control={form.control}
                name={`${name}.${index}.label`}
                render={({ field }) => (
                  <FormItem className="min-w-48 flex-1">
                    <FormControl>
                      <Input
                        {...field}
                        maxLength={80}
                        aria-label={t('parcels.label')}
                        disabled={disabled}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`${name}.${index}.carriesItems`}
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-2 pt-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        disabled={disabled}
                        onCheckedChange={(checked) => field.onChange(checked === true)}
                      />
                    </FormControl>
                    <FormLabel className="font-normal">{t('parcels.carriesItems')}</FormLabel>
                  </FormItem>
                )}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t('parcels.remove', { label: label || index + 1 })}
                disabled={disabled}
                onClick={() => remove(index)}
              >
                <Trash2 aria-hidden />
              </Button>
            </li>
          );
        })}
      </ul>
      {arrayError ? (
        <p role="alert" className="text-destructive text-sm">
          {arrayError}
        </p>
      ) : null}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="self-start"
        disabled={disabled || fields.length >= MAX_PARCELS}
        onClick={() => append({ key: '', label: '', carriesItems: fields.length === 0 })}
      >
        <Plus aria-hidden />
        {t('parcels.add')}
      </Button>
    </fieldset>
  );
}
